import React, { useState } from "react";
import { toast } from "sonner";
import { Sparkles, Loader2, Check } from "lucide-react";
import { api } from "../../lib/api";
import { Modal, inputCls } from "./ui";

// AI-assisted category discovery: paste a store link -> AI suggests
// categories & sub-categories -> tick the ones you want -> add to the taxonomy.
export function AISuggestCategoriesModal({ store = "", website = "", onClose, onApplied }) {
  const [url, setUrl] = useState(website || "");
  const [name, setName] = useState(store || "");
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [cats, setCats] = useState(null); // [{name, subcategories:[], exists}]
  const [picked, setPicked] = useState({}); // {catName: {sel:bool, subs:Set}}

  const suggest = async () => {
    setLoading(true); setCats(null);
    try {
      const { data } = await api.post("/admin/ai/suggest-categories", { url, store: name });
      setCats(data.categories);
      const init = {};
      data.categories.forEach((c) => { init[c.name] = { sel: true, subs: new Set(c.subcategories) }; });
      setPicked(init);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not suggest categories. Add the store website URL.");
    } finally { setLoading(false); }
  };

  const toggleCat = (n) => setPicked((p) => ({ ...p, [n]: { ...p[n], sel: !p[n].sel } }));
  const toggleSub = (n, s) => setPicked((p) => {
    const subs = new Set(p[n].subs);
    subs.has(s) ? subs.delete(s) : subs.add(s);
    return { ...p, [n]: { ...p[n], subs } };
  });

  const apply = async () => {
    const selected = (cats || [])
      .filter((c) => picked[c.name]?.sel)
      .map((c) => ({ name: c.name, subcategories: Array.from(picked[c.name].subs) }));
    if (!selected.length) { toast.error("Select at least one category."); return; }
    setApplying(true);
    try {
      const { data } = await api.post("/admin/categories/bulk", { categories: selected });
      toast.success(`Added ${data.added} · merged ${data.merged} categories`);
      onApplied?.();
    } catch { toast.error("Failed to add categories"); }
    finally { setApplying(false); }
  };

  const chip = "text-xs rounded-full px-2.5 py-1 border cursor-pointer select-none";

  return (
    <Modal title="AI categories from a store link" onClose={onClose} testid="ai-categories-modal">
      <div className="space-y-3">
        <p className="text-sm text-[#5C5C5C]">Paste a store or supplier link (or just the name). AI will suggest the categories &amp; sub-categories they sell — tick the ones you want to add.</p>
        <input className={inputCls} placeholder="Store name (e.g. Boots UK)" value={name} onChange={(e) => setName(e.target.value)} data-testid="aic-store" />
        <div className="flex gap-2">
          <input className={inputCls} placeholder="Store link (e.g. https://www.boots.com)" value={url} onChange={(e) => setUrl(e.target.value)} data-testid="aic-url" />
          <button type="button" onClick={suggest} disabled={loading || (!url && !name)} data-testid="aic-suggest"
            className="shrink-0 flex items-center gap-1.5 bg-[#306D29] hover:bg-[#0D530E] text-white font-semibold px-4 rounded-xl disabled:opacity-60" style={{ transition: "background-color 0.2s ease" }}>
            {loading ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />} Suggest
          </button>
        </div>

        {loading && <p className="text-sm text-[#5C5C5C]">Analysing the store… this takes a few seconds.</p>}

        {cats && (
          <div className="max-h-[46vh] overflow-y-auto space-y-3 pr-1" data-testid="aic-results">
            {cats.map((c) => {
              const p = picked[c.name] || { sel: false, subs: new Set() };
              return (
                <div key={c.name} className={`rounded-xl border p-3 ${p.sel ? "border-[#306D29] bg-[#306D29]/5" : "border-black/10"}`}>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={p.sel} onChange={() => toggleCat(c.name)} data-testid={`aic-cat-${c.name}`} />
                    <span className="font-semibold text-[#0D530E]">{c.name}</span>
                    {c.exists && <span className="text-[10px] uppercase tracking-wide bg-[#E7E1B1] text-[#0D530E] rounded-full px-2 py-0.5">exists · will merge</span>}
                  </label>
                  {p.sel && c.subcategories.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mt-2 pl-6">
                      {c.subcategories.map((s) => {
                        const on = p.subs.has(s);
                        return (
                          <span key={s} onClick={() => toggleSub(c.name, s)} data-testid={`aic-sub-${c.name}-${s}`}
                            className={`${chip} ${on ? "bg-[#0D530E] text-[#FBF5DD] border-[#0D530E]" : "bg-white text-[#0D530E] border-black/15"}`}>
                            {on && <Check size={11} className="inline mr-1 -mt-0.5" />}{s}
                          </span>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
            <button type="button" onClick={apply} disabled={applying} data-testid="aic-apply"
              className="w-full bg-[#306D29] hover:bg-[#0D530E] text-white font-semibold py-3 rounded-full disabled:opacity-60 sticky bottom-0" style={{ transition: "background-color 0.2s ease" }}>
              {applying ? "Adding…" : "Add selected categories"}
            </button>
          </div>
        )}
      </div>
    </Modal>
  );
}
